import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";

export type Simulation = {
  id: string;
  slug: string;
  title: string;
  summary: string;
  practice_area: string;
  difficulty: "beginner" | "intermediate" | "advanced";
  duration_min: number;
  role_played: string;
  scenario_intro: string;
  learning_outcomes: string[];
  competencies: Array<{ key: string; label: string }>;
  document_pack: Array<{ title: string; body: string }>;
  tasks: Array<{ key: string; title: string; brief: string; rubric?: string[] }>;
  xp_reward: number;
};

function publicClient() {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  const url = process.env.SUPABASE_URL!;
  return createClient(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

export const listPublishedSimulations = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) => z.object({ limit: z.number().int().min(1).max(200).default(30) }).parse(i))
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: rows, error } = await sb
      .from("lxp_simulations")
      .select("id,slug,title,summary,practice_area,difficulty,duration_min,role_played,scenario_intro,learning_outcomes,competencies,document_pack,tasks,xp_reward")
      .eq("published", true)
      .order("created_at", { ascending: false })
      .limit(data.limit);
    if (error) throw new Error(error.message);
    return { items: (rows ?? []) as Simulation[] };
  });

export const getSimulationBySlug = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) => z.object({ slug: z.string().min(1).max(200) }).parse(i))
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: row, error } = await sb
      .from("lxp_simulations")
      .select("id,slug,title,summary,practice_area,difficulty,duration_min,role_played,scenario_intro,learning_outcomes,competencies,document_pack,tasks,xp_reward")
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return { simulation: (row as Simulation | null) ?? null };
  });
